import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';
import { Error } from '../../models/error.model';
import { AppServiceToken } from '../../models/app.service.model';
import { CompanyToken } from '../../models/company.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Contact } from '../../models/contact.model';
import { legendValidInvalidRestart, legendValidRequiredRestart, legendIconValidInvalidRestart, legendValid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { removeUploadFile } from '../../scripts/file.load.script';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { uploadTooltip } from '../../utils/tooltip.util';
import { restartForm } from '../../utils/stepper.util';

import AppLoading from '../Loading';
import AppContactFormShortStepper from '../../interfaces/main/contact/component/ContactFormShortStepper';

export interface AppModalContactCreateProps {
  appServiceForUser: AppServiceToken | null;
  companyForUser: CompanyToken | null,
  settingsForCompany?: any,
  setContact: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setLanguageDocument?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setQrTtypeDocument?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  loadAllContacts?: Function,
  loadContactsForCompany?: Function,
};

let errorResponse: Error, contactResponse: Contact;

const AppModalContactCreate: React.FunctionComponent<AppModalContactCreateProps> = ({appServiceForUser, companyForUser, settingsForCompany, setContact, setLanguageDocument, setQrTtypeDocument, loadAllContacts, loadContactsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [uuid, setUuid] = useState({value: '', valid: true})
  const [type, setType] = useState({value: '', valid: false})
  const [name, setName] = useState({value: '', valid: false})
  const [firstName, setFirstName] = useState({value: '', valid: false})
  const [lastName, setLastName] = useState({value: '', valid: false})
  const [logo, setLogo] = useState({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: true, message: ''}})
  const [email, setEmail] = useState({value: '', valid: true})
  const [phone, setPhone] = useState({value: '', valid: true})
  const [website, setWebsite] = useState({value: '', valid: true})
  const [street, setStreet] = useState({value: '', valid: true})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: true})
  const [additional, setAdditional] = useState({value: '', valid: true})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: true})
  const [city, setCity] = useState({value: '', valid: true})
  const [zipCode, setZipCode] = useState({value: '', valid: true})
  const [country, setCountry] = useState({value: '', valid: true})

  const submitContactCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid) {
      let companyCurrency = (companyForUser as any).currency?.code ?? ''
      let companyLanguage = (companyForUser as any).language ?? ''
      let companyQrType = settingsForCompany?.qr_type || (companyForUser as any).qr_type || 'none'
      let addressStreetNumber = (streetNumber.value.length === 0) ? 'NN' : streetNumber.value

      await ApiDigitalAdminContact.postContact(companyForUser.id, type.value, uuid.value, name.value, firstName.value, lastName.value, logo.value.file, email.value, phone.value, website.value, '', '', street.value, addressStreetNumber, additional.value, administrativeArea.value, city.value, zipCode.value, country.value, companyCurrency, '', '', '', '', companyLanguage, companyQrType, []).then( (response) => {
        if (response.status === 201) {
          contactResponse = response.data

          if (loadAllContacts) loadAllContacts(companyForUser.id)
          if (loadContactsForCompany) loadContactsForCompany(companyForUser.id)
          setContact({value: contactResponse.id, object: contactResponse, valid: true})
          legendValid('legend-customer-required')

          if (setLanguageDocument) {
            setLanguageDocument({value: contactResponse.data.language, valid: true})
            legendValid('legend-language-required')
          }
          if (setQrTtypeDocument) {
            setQrTtypeDocument({value: contactResponse.data.qr_type, valid: true})
            legendValid('legend-qr-type-required')
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedContact,
            text: lang.labels.newContactWwillAddedToCompanyAndSelectDefault,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalContactCreate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalContactCreate = () => {
    modalHide('modal-contact-create')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function uploadViewForm() {
    legendValidInvalidRestart('modal-contact-create-legend-type-required')
    legendIconValidInvalidRestart('modal-contact-create-input-name', 'modal-contact-create-legend-name-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-name', 'modal-contact-create-legend-name-required')
    legendIconValidInvalidRestart('modal-contact-create-input-first-name', 'modal-contact-create-legend-first-name-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-first-name', 'modal-contact-create-legend-first-name-required')
    legendIconValidInvalidRestart('modal-contact-create-input-last-name', 'modal-contact-create-legend-last-name-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-last-name', 'modal-contact-create-legend-last-name-required')
    legendValidRequiredRestart('modal-contact-create-legend-logo-type', 'modal-contact-create-legend-logo-size')
    legendIconValidInvalidRestart('modal-contact-create-input-email', 'modal-contact-create-legend-email-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-phone', 'modal-contact-create-legend-phone-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-website', 'modal-contact-create-legend-website-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-street', 'modal-contact-create-legend-street-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-street-number', 'modal-contact-create-legend-street-number-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-additional', 'modal-contact-create-legend-additional-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-administrative-area', 'modal-contact-create-legend-administrative-area-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-city', 'modal-contact-create-legend-city-valid')
    legendIconValidInvalidRestart('modal-contact-create-input-zip-code', 'modal-contact-create-legend-zip-code-valid')
  }

  function uploadDataForm() {
    setType({value: '', valid: false})
    setName({value: '', valid: false})
    setFirstName({value: '', valid: false})
    setLastName({value: '', valid: false})
    setEmail({value: '', valid: true})
    setPhone({value: '', valid: true})
    setWebsite({value: '', valid: true})
    setStreet({value: '', valid: true})
    setStreetNumber({value: '', valid: true})
    setAdditional({value: '', valid: true})
    setAdministrativeArea({value: '', valid: true})
    setCity({value: '', valid: true})
    setZipCode({value: '', valid: true})
    setLogo(removeUploadFile())
    setCountry({value: 'CH', valid: true})
  }

  function restartModalForm() {
    setUuid({value: '', valid: true})
    uploadDataForm()
    uploadViewForm()
  }

  function restartModal() {
    restartForm('container-stepper-form', ['form-title-1', 'form-title-2', 'form-title-3'], ['form-container-1', 'form-container-2', 'form-container-3'])
    legendIconValidInvalidRestart('modal-contact-create-input-uuid', 'modal-contact-create-legend-uuid-valid')
    restartModalForm()
  }

  useEffect( () => {
    uploadDataForm()
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  return (
    <div id="modal-contact-create" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.createNewContact}</h2>
              <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={executeHideModalContactCreate}>
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body">
            { appServiceForUser && companyForUser
              ?
              <AppContactFormShortStepper loadIndicator={loadIndicator} uuid={uuid} type={type} name={name} firstName={firstName} lastName={lastName} logo={logo} email={email} phone={phone} website={website} street={street} streetNumber={streetNumber} additional={additional} administrativeArea={administrativeArea} city={city} zipCode={zipCode} country={country} setUuid={setUuid} setType={setType} setName={setName} setFirstName={setFirstName} setLastName={setLastName} setLogo={setLogo} setEmail={setEmail} setPhone={setPhone} setWebsite={setWebsite} setStreet={setStreet} setStreetNumber={setStreetNumber} setAdditional={setAdditional} setAdministrativeArea={setAdministrativeArea} setCity={setCity} setZipCode={setZipCode} setCountry={setCountry} submitContactCreate={submitContactCreate} uploadDataForm={uploadDataForm} uploadViewForm={uploadViewForm} appServiceForUserProp={appServiceForUser} companyForUserProp={companyForUser}></AppContactFormShortStepper>
              :
              <AppLoading></AppLoading>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalContactCreate;
